<template>
  <div class="body select-role-page" dir="rtl">
    <div v-if="parentSelectionOpen" class="parent-choice-overlay" @click.self="cancelParentSelection">
      <section class="parent-choice-dialog" role="dialog" aria-modal="true" aria-labelledby="parent-choice-title">
        <h2 id="parent-choice-title">انتخاب حساب والد</h2>
        <p>برای این شماره چند حساب پیدا شد. کد موردنظر را انتخاب کنید.</p>
        <label v-for="parent in parentMatches" :key="parent.id_subcode" class="parent-choice-option">
          <input v-model="selectedParentSubcode" type="radio" :value="String(parent.id_subcode)">
          <span><strong>{{ parent.title || 'بدون نام' }}</strong><small>کد: {{ parent.id_subcode }}</small><small v-if="parent.address && parent.address !== '-'">{{ parent.address }}</small></span>
        </label>
        <div class="parent-choice-actions">
          <button type="button" @click="cancelParentSelection">انصراف</button>
          <button type="button" class="primary" :disabled="!selectedParentSubcode" @click="continueWithParentSelection">ادامه</button>
        </div>
      </section>
    </div>
    <!-- Header -->
    <header class="app-header">
      <div class="header-content">
        <div class="header-left">
          <button v-if="showBackButton" @click="goBack" class="btn-back">
            →
          </button>

          <div class="logo-icon">
            <img class="logo-icon" src="@/assets/DiamondLogo.png" />
          </div>

          <a class="navbar-brand mb-0 me-2" style="margin-left: auto;" href="#">
            {{ $t('select_role.app.brand_name') }}
          </a>
        </div>

        <div @click="logout" class="btn-logout" style="width: 20px; height: 20px; margin-right: auto;">
          <img style="width: 20px; height: 20px;" src="@/assets/Icons/logout.png" alt="" />
        </div>
      </div>
    </header>

    <!-- Switching Role Note -->
    <div class="role-note" v-if="isSwitchingRole">
      <p>{{ $t('select_role.role.switching_message') }}</p>
    </div>

    <!-- Company Info Section -->
    <div class="company-info-section" v-if="selectedCompany">
      <div class="role-item" @click="goToLandingCompany">
        <div class="role-avatar">
          <img src="@/assets/Icons/companyIcon.png" style="width: 60px; height: 60px;" />
        </div>

        <div class="role-content">
          <div class="role-top-row">
            <div class="role-title-wrapper">
              <span class="role-title">
                {{ $t('select_role.company.title') }} {{ selectedCompany.company_name }}
              </span>
              <span>({{ $t('select_role.company.landing_panel') }})</span>
            </div>
          </div>

          <div class="role-bottom-row">
            <span class="role-description">
              {{ selectedCompany.description }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Database Warning Section after Company Info -->
    <div v-if="selectedCompany && !selectedCompany.db_accessible" class="db-warning-section">
      <div class="warning-card">
        <div class="warning-icon">⚠️</div>
        <div class="warning-content">
          <h4>{{ $t('select_role.db_warning.title', 'اتصال به پایگاه داده امکان پذیر نیست') }}</h4>
          <p>{{ $t('select_role.db_warning.message', 'در حال حاضر امکان اتصال به پایگاه داده این شرکت وجود ندارد.') }}</p>
          <p class="warning-note">{{ $t('select_role.db_warning.note', 'شما می‌توانید از پنل معرفی استفاده کنید.') }}</p>
        </div>
      </div>
    </div>

    <!-- Role List -->
    <div class="role-list-container">
      <!-- Company Selector (If allowed) -->
      <div v-if="showCompanySelector" class="company-selector-section">
        <h3 class="section-title">{{ $t('select_role.company.select_your_company') }}</h3>

        <select v-model="selectedCompanyId" @change="onCompanyChange" class="company-select">
          <option value="">
            {{ $t('select_role.company.choose_company') }}
          </option>

          <option
            v-for="company in companies"
            :key="company.company_id"
            :value="company.company_id"
          >
            {{ company.company_name }}
          </option>
        </select>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <span>{{ $t('select_role.global.loading') }}</span>
      </div>

      <!-- Roles Available -->
      <div v-else-if="availableRoles.length > 0" class="role-list">
        <div 
          v-for="role in availableRoles" 
          :key="role"
          class="role-item"
          :class="{ 
            'disabled-role': role === 'operator' && !hasCompanySelected,
            'selected-role': selectedRole === role
          }"
          @click="selectRole(role)"
        >
          <div class="role-avatar">
            <img src="@/assets/Icons/roleIcon.png" style="width: 60px; height: 60px;" />
          </div>

          <div class="role-content">
            <div class="role-top-row">
              <div class="role-title-wrapper">
                <span class="role-title">
                  {{ $t('select_role.roles.names.' + role) }}
                </span>
              </div>
            </div>

            <div class="role-bottom-row">
              <span class="role-description">
                {{ $t('select_role.roles.descriptions.' + role) }}
              </span>

              <span
                v-if="role === 'operator' && !hasCompanySelected"
                class="role-warning"
              >
                {{ $t('select_role.roles.need_company') }}
              </span>

              <span 
                v-if="selectedRole === role" 
                class="role-check"
              >
                ✓
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- No Roles -->
      <div v-else class="empty-state">
        <div class="empty-icon">🎭</div>
        <p>{{ $t('select_role.roles.no_roles') }}</p>

        <button @click="logout" class="btn-primary">
          {{ $t('select_role.auth.logout') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRouter, useRoute } from 'vue-router'

export default {
  name: 'SelectRole',
  setup() {
    const authStore = useAuthStore()
    const router = useRouter()
    const route = useRoute()
    const selectedCompanyId = ref(authStore.getSelectedCompanyId || '')
    const selectedRole = ref(null)
    const parentSelectionOpen = ref(false)
    const parentMatches = ref([])
    const selectedParentSubcode = ref('')
    const pendingParentCompany = ref(null)
    
    // Check if we're switching roles (came from role switcher)
    const isSwitchingRole = ref(route.query.switch === 'true')
    
    const availableRoles = computed(() => {
      return authStore.getAvailableRoles.filter(role => role !== 'VIEWER' && role != 'store' && role != 'employee')
    })
    const companies = computed(() => authStore.companies)
    const selectedCompany = computed(() => authStore.selectedCompany)
    const hasCompanySelected = computed(() => authStore.hasCompanySelected)
    const operatorData = computed(() => authStore.operatorData)
    
    const showCompanySelector = computed(() => 
      companies.value.length > 0 && !hasCompanySelected.value
    )
    
    const showBackButton = computed(() => {
      return window.history.length > 1
    })
    
    const getRoleDisplayName = (role) => {
      const names = {
        'customer': 'مشتری',
        'employer': 'مدیریت',
        'admin': 'مدیر سیستم',
        'store': 'فروش',
        'operator': 'اپراتور',
        'office': 'اتوماسیون اداری',
      }
      return names[role] || role
    }
    
    const getRoleDescription = (role) => {
      const descriptions = {
        'customer': 'مدیریت حساب کاربری و مشاهده خدمات',
        'employer': 'مدیریت پروژه ها و نیروی کار',
        'admin': 'مدیریت کامل سیستم و کاربران',
        'store': 'پنل فروش',
        'operator': 'پنل اپراتور و پشتیبانی',
        'office': 'پنل مدیریت نامه های اداری',
      }
      return descriptions[role] || 'دسترسی به پنل کاربری'
    }
    
    const getRoleIcon = (role) => {
      const icons = {
        'customer': 'fas fa-user',
        'employer': 'fas fa-briefcase',
        'admin': 'fas fa-user-tie',
        'store': 'fas fa-chart-line',
        'operator': 'fas fa-headset',
        'office': 'fas fa-headset',
      }
      return icons[role] || 'fas fa-user'
    }
    
    const onCompanyChange = async (selectedSubcode = null) => {
      if (selectedCompanyId.value) {
        const company = companies.value.find(c => c.company_id == selectedCompanyId.value)
        if (company) {
          try {
            const response = await authStore.selectCompany(company.company_id, company.company_name, selectedSubcode)
            if (response?.requires_parent_selection) {
              pendingParentCompany.value = company
              parentMatches.value = response.parent_matches || []
              selectedParentSubcode.value = ''
              parentSelectionOpen.value = true
              return
            }
            alert(`شرکت ${company.company_name} با موفقیت انتخاب شد`)
          } catch (error) {
            console.error('Company selection failed:', error)
            alert('خطا در انتخاب شرکت')
          }
        }
      }
    }
    
    const cancelParentSelection = () => {
      parentSelectionOpen.value = false
      parentMatches.value = []
      selectedParentSubcode.value = ''
      pendingParentCompany.value = null
      selectedCompanyId.value = authStore.getSelectedCompanyId || ''
    }

    const continueWithParentSelection = async () => {
      if (!selectedParentSubcode.value || !pendingParentCompany.value) return
      const subcode = selectedParentSubcode.value
      parentSelectionOpen.value = false
      await onCompanyChange(subcode)
      parentMatches.value = []
      pendingParentCompany.value = null
    }

    const selectRole = (role) => {
      if (role === 'operator' && !hasCompanySelected.value) {
        alert('لطفا ابتدا یک شرکت را انتخاب کنید')
        return
      }
      
      selectedRole.value = role
      authStore.setSelectedRole(role)
      
      if (role === 'operator') {
        if (!operatorData.value) {
          console.error('No operator data found')
          return
        }
        
        router.push({
          path: '/operator-panel',
          query: {
            department: operatorData.value.categories?.[0]?.type || 'support',
            companyId: operatorData.value.company_id,
            companyName: operatorData.value.company_name
          }
        })
      } 
      else if (role === 'driver'){
        router.push('/peak/register-driver')
      }
      else if (role === 'requester'){
        router.push('/peak/customer-delivery')
      }
       else {
        router.push(getRoleDashboard(role))
      }
    }
    
    const getRoleDashboard = (role) => {
      console.log('role ' + role)
      switch(role) {
        case 'customer': return '/dashboard'
        case 'employer': return '/employer/dashboard'
        case 'admin': return '/admin/dashboard'
        case 'store': return '/sales/kala-store'
        case 'accountant': return '/accountant/dashboard'
        case 'office': return '/office/dashboard'
        case 'employee': return '/employee/dashboard'
        default: return '/dashboard'
      }
    }
    
    const goBack = () => {
      router.push('/select-company')
    }

    const goToLandingCompany = () => {
      router.push('/company/' + selectedCompany.value.company_name)
    }
    
    const logout = () => {
      authStore.logout()
      router.push('/login')
    }
    
    return {
      availableRoles,
      companies,
      selectedCompany,
      hasCompanySelected,
      showCompanySelector,
      selectedCompanyId,
      selectedRole,
      isSwitchingRole,
      showBackButton,
      operatorData,
      getRoleDisplayName,
      goToLandingCompany,
      getRoleDescription,
      getRoleIcon,
      selectRole,
      onCompanyChange,
      parentSelectionOpen,
      parentMatches,
      selectedParentSubcode,
      cancelParentSelection,
      continueWithParentSelection,
      goBack,
      logout
    }
  }
}
</script>

<style scoped>
.select-role-page {
  min-height: 100vh;
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  direction: rtl;
}

.parent-choice-overlay { position: fixed; z-index: 20000; inset: 0; display: grid; place-items: center; padding: 20px; background: rgba(15, 23, 42, .58); backdrop-filter: blur(4px); }
.parent-choice-dialog { width: min(100%, 500px); max-height: 82vh; overflow: auto; padding: 24px; border-radius: 16px; background: var(--bg, #fff); color: var(--text, #172033); box-shadow: 0 24px 70px rgba(15, 23, 42, .25); }
.parent-choice-dialog > p { color: var(--text-muted, #64748b); line-height: 1.8; }
.parent-choice-option { display: flex; gap: 12px; margin: 10px 0; padding: 13px; border: 1px solid var(--border, #dbe2ea); border-radius: 11px; cursor: pointer; }
.parent-choice-option input { margin-top: 4px; accent-color: var(--primary, #4f46e5); }
.parent-choice-option span { display: grid; gap: 4px; }
.parent-choice-option small { color: var(--text-muted, #64748b); }
.parent-choice-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px; }
.parent-choice-actions button { min-width: 100px; padding: 10px 16px; border: 0; border-radius: 9px; cursor: pointer; }
.parent-choice-actions .primary { background: var(--primary, #4f46e5); color: white; }

.app-header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1rem 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-left h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #2d3748;
}

.btn-logout {
  padding: 0.5rem 1.5rem;
  border: none;
  border-radius: 8px;
  color: #e53e3e;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-logout:hover {
  background: #feb2b2;
  transform: translateY(-2px);
}

.role-note {
  max-width: 1200px;
  margin: 1rem auto 0;
  padding: 0 2rem;
}

.role-note p {
  background: #ebf8ff;
  color: #3182ce;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border-right: 4px solid #3182ce;
  margin: 0;
  font-size: 0.95rem;
}

/* Company Info Section */
.company-info-section {
  max-width: 800px;
  margin: 1.5rem auto 0;
  padding: 0 2rem;
}

.company-info-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  transition: transform 0.3s;
}

.company-info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
}

.company-avatar {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #667eea;
  box-shadow: 0 4px 10px rgba(102, 126, 234, 0.3);
  flex-shrink: 0;
}

.company-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.company-details {
  flex: 1;
}

.company-name {
  font-size: 1.4rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
}

.company-description {
  color: #718096;
  font-size: 0.95rem;
  margin: 0 0 0.75rem 0;
  line-height: 1.5;
}

.company-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: #c6f6d5;
  color: #22543d;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.company-badge i {
  font-size: 0.9rem;
}

.role-list-container {
  max-width: 800px;
  margin: 2rem auto;
  padding: 0 2rem;
}

.company-selector-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.section-title {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  color: #2d3748;
}

.company-select {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s;
}

.company-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.loading-state {
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.role-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.role-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s;
  border: 2px solid transparent;
  position: relative;
}

.role-item:hover:not(.disabled-role) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: #667eea;
}

.role-item.selected-role {
  border-color: #48bb78;
  background: #f0fff4;
}

.role-item.disabled-role {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f7fafc;
}

.role-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.role-content {
  flex: 1;
  min-width: 0;
}

.role-top-row {
  margin-bottom: 0.5rem;
}

.role-title-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.role-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
}

.role-badge {
  background: #e7f3ff;
  color: #3182ce;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.role-badge.admin {
  background: #f8d7da;
  color: #c53030;
}

.role-bottom-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.role-description {
  color: #718096;
  font-size: 0.9rem;
  line-height: 1.5;
}

.role-warning {
  color: #e53e3e;
  font-size: 0.8rem;
  background: #fed7d7;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  white-space: nowrap;
}

.role-check {
  color: #48bb78;
  font-size: 1.25rem;
  font-weight: bold;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0fff4;
  border-radius: 50%;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state p {
  color: #718096;
  margin-bottom: 1.5rem;
}

.btn-primary {
  padding: 0.75rem 2rem;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary:hover {
  background: #5a67d8;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* Responsive Design */
@media (max-width: 768px) {
  .app-header {
    padding: 1rem;
  }
  
  .header-left h2 {
    font-size: 1.2rem;
  }
  
  .company-info-card {
    flex-direction: column;
    text-align: center;
    padding: 1.5rem;
  }
  
  .company-avatar {
    width: 60px;
    height: 60px;
  }
  
  .company-name {
    font-size: 1.2rem;
  }
  
  .role-list-container {
    padding: 0 1rem;
  }
  
  .role-item {
    padding: 1rem;
  }
  
  .role-avatar {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }
  
  .role-title {
    font-size: 1rem;
  }
  
  .role-description {
    font-size: 0.85rem;
  }
  
  .role-bottom-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .role-warning {
    align-self: flex-start;
  }
}

/* Animation for new items */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.role-item {
  animation: slideIn 0.3s ease-out;
}

.role-item:nth-child(1) { animation-delay: 0.05s; }
.role-item:nth-child(2) { animation-delay: 0.1s; }
.role-item:nth-child(3) { animation-delay: 0.15s; }
.role-item:nth-child(4) { animation-delay: 0.2s; }
.role-item:nth-child(5) { animation-delay: 0.25s; }

.select-role-page {
  min-height: 100vh;
  direction: rtl;
  background: var(--bg);
  color: var(--text);
}

/* HEADER */
.app-header {
  background: var(--card-bg);
  backdrop-filter: blur(10px);
  padding: 1rem 2rem;
  box-shadow: 0 4px 20px var(--shadow-1);
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid var(--card-border);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-left h2 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--heading);
}

.btn-logout {
  padding: 0.5rem 1.5rem;
  border: none;
  border-radius: 8px;
  color: var(--orange-2);
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  transition: all 0.3s;
  display: flex;
  align-items: center;
}

.btn-logout:hover {
  background: var(--hover);
  transform: translateY(-2px);
}

/* SWITCH ROLE ALERT */
.role-note {
  max-width: 1200px;
  margin: 1rem auto 0;
  padding: 0 2rem;
}

.role-note p {
  background: var(--ring);
  color: var(--primary);
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border-right: 4px solid var(--primary);
  margin: 0;
  font-size: 0.95rem;
}

/* COMPANY INFO */
.company-info-section {
  max-width: 800px;
  margin: 1.5rem auto 0;
  padding: 0 2rem;
}

.company-info-card {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  border: 1px solid var(--card-border);
  box-shadow: 0 8px 25px var(--shadow-1);
  transition: transform 0.3s;
}

.company-info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px var(--shadow-2);
}

.company-avatar {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid var(--primary);
  box-shadow: 0 4px 10px var(--shadow-2);
}

.company-details {
  flex: 1;
}

.company-name {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--heading);
}

.company-description {
  color: var(--subtext);
  font-size: 0.95rem;
  line-height: 1.5;
}

.company-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: var(--green);
  color: var(--text);
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

/* ROLE LIST CONTAINER */
.role-list-container {
  max-width: 800px;
  margin: 2rem auto;
  padding: 0 2rem;
}

/* COMPANY SELECT */
.company-selector-section {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 15px var(--shadow-1);
  border: 1px solid var(--card-border);
}

.section-title {
  font-size: 1.1rem;
  color: var(--heading);
}

.company-select {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid var(--border);
  border-radius: 8px;
  background: var(--card-bg);
  color: var(--text);
  cursor: pointer;
  transition: all 0.3s;
}

.company-select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--ring);
}

/* LOADING */
.loading-state {
  text-align: center;
  padding: 3rem;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 4px 15px var(--shadow-1);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

/* ROLE ITEMS */
.role-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.role-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: var(--card-bg);
  border-radius: 12px;
  padding: 1.25rem;
  border: 2px solid transparent;
  box-shadow: 0 4px 15px var(--shadow-1);
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
}

.role-item:hover:not(.disabled-role) {
  transform: translateY(-2px);
  border-color: var(--primary);
  box-shadow: 0 8px 25px var(--shadow-2);
}

.role-item.selected-role {
  border-color: var(--green);
  background: rgba(16,185,129,0.1);
}

.role-item.disabled-role {
  opacity: 0.6;
  cursor: not-allowed;
  background: var(--drop-down);
}

.role-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--gradient-1), var(--gradient-2));
  display: flex;
  justify-content: center;
  align-items: center;
  color: #fff;
}

.role-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--heading);
}

.role-description {
  color: var(--subtext);
  font-size: 0.9rem;
  line-height: 1.5;
}

.role-warning {
  color: var(--orange-2);
  background: var(--hover);
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
}

.role-check {
  color: var(--green);
  font-size: 1.25rem;
}

/* EMPTY STATE */
.empty-state {
  text-align: center;
  padding: 3rem;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 4px 15px var(--shadow-1);
}

.empty-state p {
  color: var(--subtext);
}

.btn-primary {
  padding: 0.75rem 2rem;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  transition: 0.3s;
}

.btn-primary:hover {
  background: var(--primary-hover);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px var(--shadow-2);
}

.db-warning-section {
  margin: 20px;
  animation: slideDown 0.3s ease;
}

.warning-card {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}

.warning-icon {
  font-size: 30px;
}

.warning-content h4 {
  margin: 0 0 8px 0;
  color: #856404;
}

.warning-content p {
  margin: 5px 0;
  color: #856404;
}

.warning-note {
  font-size: 12px;
  opacity: 0.8;
}

.intro-role {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: 2px solid #764ba2;
}

.intro-role .role-title,
.intro-role .role-description {
  color: white !important;
}

.role-info {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 8px;
  border-radius: 20px;
  margin-left: 10px;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
